import { ReadSyncableStateStatisticsDocument } from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { toSyncParameters } from 'modules/integrations/lib/parameters'
import { usePolling } from 'modules/integrations/hooks/usePolling'
import { useCallback, useMemo } from 'react'

interface UseSyncableStateStatisticsOptions {
  skip?: boolean
  pollingIntervalMs?: number | null
}

export const useSyncableStateStatistics = ({
  skip = false,
  pollingIntervalMs = null,
}: UseSyncableStateStatisticsOptions) => {
  const {
    syncEnabled,
    marketplaceIntegration,
    configuration,
    dataCollectionBaseGraphqlJsonString,
    companyId,
    contextParameters,
    isBookkeeperView,
  } = usePrivateIntegrationsSyncContext()

  const [fetchSyncableStateStatistics, { data, loading }] = useLazyQuery(
    ReadSyncableStateStatisticsDocument
  )

  const serializedContextParameters = JSON.stringify(contextParameters)

  const canFetch = !skip && !!syncEnabled && !!marketplaceIntegration && !!companyId

  const isPollingEnabled = canFetch && pollingIntervalMs != null && pollingIntervalMs > 0

  const graphqlVariablesJson = useMemo(() => {
    if (!syncEnabled || dataCollectionBaseGraphqlJsonString == null || !marketplaceIntegration)
      return undefined

    const base = JSON.parse(dataCollectionBaseGraphqlJsonString)
    return JSON.stringify({
      query: base.query,
      variables: {
        ...base.variables,
        marketplaceIntegrationId: Number(marketplaceIntegration.id),
        includeSyncableState: true,
        syncableViaIntegrations: true,
      },
    })
  }, [syncEnabled, dataCollectionBaseGraphqlJsonString, marketplaceIntegration?.id])

  const fetchStatistics = useCallback(
    (options?: { fetchPolicy?: 'network-only' }) => {
      if (!companyId || !marketplaceIntegration) return

      const parsedContextParameters = JSON.parse(serializedContextParameters)

      fetchSyncableStateStatistics({
        variables: {
          origin: configuration.origin,
          parameters: toSyncParameters(parsedContextParameters),
          graphqlVariablesJson,
          companyId,
          marketplaceIntegrationId: marketplaceIntegration.id,
        },
        context: {
          isBookkeeperView,
        },
        ...options,
      })
    },
    [
      companyId,
      marketplaceIntegration,
      configuration.origin,
      serializedContextParameters,
      graphqlVariablesJson,
      isBookkeeperView,
      fetchSyncableStateStatistics,
    ]
  )

  // Fetch on mount and when dependencies change.
  // usePolling only handles subsequent interval-based refreshes.
  useUnsafeEffect(() => {
    if (!canFetch) return
    fetchStatistics()
  }, [canFetch, fetchStatistics])

  const pollCallback = useCallback(() => {
    fetchStatistics({ fetchPolicy: 'network-only' })
  }, [fetchStatistics])

  usePolling({
    enabled: isPollingEnabled,
    callback: pollCallback,
    intervalMs: pollingIntervalMs ?? 0,
  })

  const refetch = useCallback(() => {
    if (!canFetch) return
    pollCallback()
  }, [canFetch, pollCallback])

  const statistics = data?.integrations?.syncableStateStatisticsConnection?.nodes?.[0]
  const resolvedStatistics = canFetch ? statistics : null

  return { statistics: resolvedStatistics, loading, refetch }
}
